import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { AdvancedConditions, CosmeticOptions, CustomRule, Filter, PreviewCustomRule } from '../../../models/filter-models';
import { FormsModule } from '@angular/forms';
import { AdvancedConditionsFilterComponent } from '../../../advanced-conditions-filter/advanced-conditions-filter.component';
import { ItemDataService } from '../../../services/item-data.service';
import { ItemData, Tier } from '../../../models/item-data-models';
import { TierSelectorComponent } from '../../select/select-tier/select-tier.component';
import { SelectRarityComponent } from '../../select/select-rarity/select-rarity.component';
import { SelectComparatorComponent } from '../../select/select-comparator/select-comparator.component';
import { SelectItemAndBaseTypeComponent } from '../../select/select-item-and-base-type/select-item-and-base-type.component';
import { BaseComponent } from '../../base.component';
import { CosmeticControls } from '../../../cosmetic-tab/cosmetic-controls.model';
import { CosmeticControlsComponent } from '../../../cosmetic-tab/cosmetic-controls/cosmetic-controls.component';
import { CosmeticSettingsComponent } from '../../../cosmetic-tab/cosmetic-settings/cosmetic-settings.component';
import { CosmeticStyleOptionsMenuComponent } from '../../cosmetic-style-options-menu/cosmetic-style-options-menu.component';
import { CosmeticStyleClipboardService } from '../../../services/cosmetic-style-clipboard.service';

@Component({
  selector: 'app-custom-rule',
  imports: [
    FormsModule,
    AdvancedConditionsFilterComponent,
    TierSelectorComponent,
    SelectRarityComponent,
    SelectComparatorComponent,
    SelectItemAndBaseTypeComponent,
    CosmeticControlsComponent,
    CosmeticSettingsComponent,
    CosmeticStyleOptionsMenuComponent,
  ],
  templateUrl: './custom-rule.component.html',
  styleUrls: ['../custom-rule-groups-shared.scss'],
})
export class CustomRuleComponent extends BaseComponent {
  @Input() cosmetic = false;
  @Input() filter?: Filter;
  @Input() isFirstRule = false;
  @Input() isLastRule = false;

  @Input({ required: true }) set reorder(value: boolean) {
    this._reorder = value;
    if (value) {
      this.advancedConditionsOpen = false;
      if (this.cosmetic) {
        this.cosmeticControls.highlightOptionsOpen = false;
        this.cosmeticControls.labelOptionsOpen = false;
        this.cosmeticControls.soundOptionsOpen = false;
      }
    }
  }
  get reorder(): boolean {
    return this._reorder;
  }
  private _reorder!: boolean;

  @Input({ required: true }) set rule(value: CustomRule) {
    this._rule = value;
    this.checkForChanges();
  }
  get rule(): CustomRule {
    return this._rule;
  }
  private _rule!: CustomRule;

  @Input() groupActive = true;
  @Input() enableRuleLabels = false;

  get ruleControlsDisabled(): boolean {
    return !this.rule.displayOptions.active || !this.groupActive || this.reorder;
  }

  get cosmeticSettingsActive(): boolean {
    if (!this.groupActive) return false;
    return this.rule.displayOptions.active === undefined ? true : this.rule.displayOptions.active;
  }

  @Output('updateFilter') updateFilterEmitter = new EventEmitter<void>();
  @Output('removeRule') removeRuleEmitter = new EventEmitter<CustomRule>();
  @Output('cloneRule') cloneRuleEmitter = new EventEmitter<CustomRule>();

  Tier = Tier;
  hasAdvancedConditions = 0;
  advancedConditionsOpen = false;
  cosmeticControls = new CosmeticControls();
  dirtyForceUpdate = 0;

  showGoldenBorder = false;

  itemData!: ItemData[];
  currencyItemTypes!: string[];

  private readonly styleClipboard = inject(CosmeticStyleClipboardService);

  constructor(private itemDataService: ItemDataService) {
    super();
    this.itemData = this.itemDataService.getItemData();
    this.currencyItemTypes = this.itemDataService.getCurrencyItemTypes();
  }

  removeRule = () => this.removeRuleEmitter.emit(this.rule);
  cloneRule = () => this.cloneRuleEmitter.emit(this.rule);

  toggleAdvancedOptionsOpen() {
    if (!this.rule.advancedConditions) {
      this.rule.advancedConditions = new AdvancedConditions();
    }
    this.advancedConditionsOpen = !this.advancedConditionsOpen;
  }

  checkForAdvancedConditionChanges() {
    this.hasAdvancedConditions = this.rule.advancedConditions
      ? AdvancedConditions.hasChanges(this.rule.advancedConditions, this.rule.displayOptions?.hidden)
      : 0;
  }

  checkForChanges() {
    this.checkForAdvancedConditionChanges();
    this.showGoldenBorder = !!this.rule.fromPresets;
  }

  updateFilter() {
    this.checkForChanges();
    if (this.cosmetic) {
      this.dirtyForceUpdate++;
    }
    this.updateFilterEmitter.emit();
  }

  selectLabelText(event: FocusEvent) {
    (event.target as HTMLInputElement).select();
  }

  getExampleName(rule: PreviewCustomRule) {
    if (rule.exampleName) return rule.exampleName;

    const qualityPrefix = this.rule?.advancedConditions?.minQuality ? 'Superior ' : '';

    if (rule.baseTypes.length > 0 && !rule.baseTypes.includes('All')) return qualityPrefix + rule.baseTypes[0];

    if (rule.itemType === 'All') return qualityPrefix + 'Example';
    if (rule.itemType === 'All Gear') return qualityPrefix + 'Example Gear';
    if (rule.itemType === 'All Weapons') return qualityPrefix + 'Example Weapon';
    if (rule.itemType === 'All Armour') return qualityPrefix + 'Example Armour';
    if (rule.itemType === 'All Augments') return 'Example Augment';
    if (rule.itemType === 'All Runes') return 'Example Rune';
    if (rule.itemType === 'All Gems') return 'Example Gem';
    if (rule.itemType === 'All Uncut Gems') return 'Example Uncut Gem';

    return 'All ' + qualityPrefix + rule.itemType;
  }

  getRuleCopyOptions = () => this.rule.displayOptions.cosmeticOptions;

  getRuleCopyNameHint = () => this.rule.label;

  pasteStyle = (options: CosmeticOptions) => {
    if (!this.rule.displayOptions.cosmeticOptions) {
      this.rule.displayOptions.cosmeticOptions = new CosmeticOptions();
    }
    this.styleClipboard.pasteTo(this.rule.displayOptions.cosmeticOptions, options);
    this.updateFilter();
  };
}
